// src/components/ProfilePage.jsx
import React, { useEffect, useState } from "react";
import MenuLayout from "./MenuLayout";
import "../styles/ProfilePage.css";
import { getProfile, updateProfile, updateProfileFormData } from "../api/user";
import { getVerification } from "../api/verification";

const fields = [
  { key: "full_name", label: "نام و نام خانوادگی", icon: "/images/icons/menu-user.svg" },
  { key: "email", label: "ایمیل", icon: "/images/profile/icon-copy.svg", readOnly: true },
  { key: "phone", label: "شماره تماس", icon: "/images/icons/menu-user.svg" },
  { key: "birth_date", label: "تاریخ تولد", icon: "/images/profile/icon-calendar.svg", type: "date" },
  { key: "national_id", label: "کد ملی", icon: "/images/profile/icon-copy.svg" },
  { key: "country", label: "کشور", icon: "/images/profile/icon-calendar.svg" },
  { key: "city", label: "شهر", icon: "/images/profile/icon-copy.svg" },
  { key: "address", label: "آدرس", icon: "/images/profile/icon-copy.svg" },
  { key: "website", label: "وب سایت", icon: "/images/profile/icon-copy.svg" },
  { key: "contact_public", label: "راه ارتباطی عمومی", icon: "/images/profile/icon-calendar.svg" },
  { key: "bio", label: "درباره من", icon: "/images/profile/icon-calendar.svg", textarea: true },
];

const ProfilePage = () => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [avatar, setAvatar] = useState("/images/profile/avatar-big.svg");
  const [status, setStatus] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [profileRes, verifyRes] = await Promise.allSettled([getProfile(), getVerification()]);
        const data = profileRes.status === "fulfilled" ? profileRes.value : {};
        setForm({
          full_name: data.user?.full_name || data.full_name || "",
          email: data.user?.email || data.email || "",
          phone: data.user?.phone || data.phone || "",
          birth_date: data.birth_date || "",
          national_id: data.national_id || "",
          country: data.country || "",
          city: data.city || "",
          address: data.address || "",
          website: data.website || "",
          contact_public: data.contact_public || "",
          bio: data.bio || "",
        });
        if (data.avatar) setAvatar(data.avatar);

        if (verifyRes.status === "fulfilled") {
          setStatus(verifyRes.value?.status || "");
        }
      } catch (err) {
        setError(err.message || "خطا در دریافت اطلاعات پروفایل");
      }
    };
    fetchData();
  }, []);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setMessage("");
    const payload = Object.entries(form).reduce((acc, [key, value]) => {
      if (key === "email") return acc;
      if (value === "") {
        acc[key] = key === "birth_date" || key === "website" ? null : "";
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});
    try {
      await updateProfile(payload);
      setMessage("تغییرات با موفقیت ذخیره شد.");
    } catch (err) {
      setError(err.message || "خطا در ذخیره پروفایل");
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const previousAvatar = avatar;
    const preview = URL.createObjectURL(file);
    setAvatar(preview);
    setError("");
    setMessage("");
    const fd = new FormData();
    fd.append("avatar", file);
    try {
      const updated = await updateProfileFormData(fd);
      if (updated?.avatar) setAvatar(updated.avatar);
      setMessage("تصویر پروفایل با موفقیت بروزرسانی شد.");
    } catch (err) {
      setAvatar(previousAvatar);
      setError(err.message || "خطا در بروزرسانی تصویر");
    } finally {
      URL.revokeObjectURL(preview);
      e.target.value = "";
    }
  };

  const statusText =
    status === "approved"
      ? "احراز هویت تایید شد"
      : status === "pending"
      ? "در انتظار بررسی احراز هویت"
      : status === "rejected"
      ? "احراز هویت رد شد (لطفا دوباره تلاش کنید)"
      : "احراز هویت ثبت نشده است";

  const statusColor =
    status === "approved" ? "#4caf50" : status === "pending" ? "#ffb300" : status === "rejected" ? "#e53935" : "#555";

  return (
    <MenuLayout>
      <div className="profile-page">
        <header className="profile-header">
          <h1 className="profile-title">پروفایل</h1>
          <p className="profile-subtitle">اطلاعات حساب کاربری خود را بروزرسانی کنید.</p>
        </header>

        <div className="profile-body">
          <section className="profile-form">
            <div className="profile-form-header">
              <h3 className="profile-form-title">اطلاعات کاربری</h3>
            </div>

            <div className="profile-grid">
              {fields.map((f) => (
                <label className="profile-field" key={f.key}>
                  <div className="profile-field-label">
                    {f.label}
                    <img src={f.icon} alt="" />
                  </div>
                  {f.textarea ? (
                    <textarea
                      value={form[f.key] || ""}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      disabled={f.readOnly}
                    />
                  ) : (
                    <input
                      type={f.type || "text"}
                      value={form[f.key] || ""}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      disabled={f.readOnly}
                    />
                  )}
                </label>
              ))}
            </div>

            {error && <div className="profile-error">{error}</div>}
            {message && <div className="profile-success">{message}</div>}

            <button className="profile-submit" onClick={handleSubmit} disabled={loading}>
              {loading ? "در حال ذخیره..." : "ذخیره تغییرات"}
            </button>
          </section>

          <div className="profile-card">
            <label className="profile-avatar" htmlFor="avatar-upload">
              <img src={avatar} alt="Avatar" className="profile-avatar-img" />
              <input
                id="avatar-upload"
                type="file"
                accept="image/*"
                className="profile-avatar-input"
                onChange={handleAvatarChange}
              />
              <span className="profile-avatar-overlay">+</span>
            </label>
            <div className="profile-card-name">{form.full_name || "نام و نام خانوادگی"}</div>
            <div className="profile-card-status" style={{ background: statusColor }}>
              وضعیت احراز هویت: {statusText}
            </div>
          </div>
        </div>
      </div>
    </MenuLayout>
  );
};

export default ProfilePage;
